import { ReactNode, useEffect, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { features } from '../../config/features';
import { useBetterAuthConvexAuth } from '../../hooks/useAppAuth';
import { authClient } from '../../lib/auth-client';
import { clearSetupOffer, hasPendingSetupOffer } from '../../lib/signInSetup';
import SignInSetupGuide from './SignInSetupGuide';
import { AuthLoading } from './AuthUi';

// The server only accepts setup on sessions under 15 minutes old. Leave a few
// minutes for the customer to finish.
const GUIDE_SESSION_WINDOW_MS = 12 * 60 * 1000;
// If the checks stall (for example on a poor connection), go to the dashboard
// rather than leave the customer looking at a spinner.
const DECISION_TIMEOUT_MS = 8000;

/**
 * Offer guided email-code setup once, straight after a password sign-in,
 * before the dashboard. It never blocks: every screen can be skipped.
 */
export default function SignInSetupGate({ children }: { children: ReactNode }) {
  const { convexUser, signOut } = useBetterAuthConvexAuth();
  const [candidate] = useState(() =>
    features.signInSetupGuideEnabled && convexUser?.role === 'customer' && hasPendingSetupOffer());
  const settings = useQuery(api.passwordlessAuth.getSettings, candidate ? {} : 'skip');
  const [sessionCreatedAt, setSessionCreatedAt] = useState<number | null | undefined>(candidate ? undefined : null);
  // Decided once, so the guide stays open while settings change underneath it.
  const [decision, setDecision] = useState<'show' | 'skip' | null>(candidate ? null : 'skip');

  useEffect(() => {
    if (!candidate) { clearSetupOffer(); return; }
    let cancelled = false;
    authClient.getSession()
      .then(result => {
        const createdAt = result.data?.session?.createdAt;
        if (!cancelled) setSessionCreatedAt(createdAt ? new Date(createdAt).getTime() : null);
      })
      .catch(() => { if (!cancelled) setSessionCreatedAt(null); });
    return () => { cancelled = true; };
  }, [candidate]);

  useEffect(() => {
    if (decision !== null) return;
    const timer = window.setTimeout(() => { clearSetupOffer(); setDecision('skip'); }, DECISION_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [decision]);

  useEffect(() => {
    if (decision !== null || settings === undefined || sessionCreatedAt === undefined) return;
    const fresh = sessionCreatedAt !== null && Date.now() - sessionCreatedAt < GUIDE_SESSION_WINDOW_MS;
    const show = settings.offerSetup && fresh;
    if (!show) clearSetupOffer();
    setDecision(show ? 'show' : 'skip');
  }, [decision, settings, sessionCreatedAt]);

  if (decision === null) return <AuthLoading />;

  if (decision === 'show' && settings) {
    return <SignInSetupGuide settings={settings}
      onFinish={() => { clearSetupOffer(); setDecision('skip'); }}
      onSignInAgain={() => { void signOut().catch(() => window.location.assign('/login')); }} />;
  }

  return <>{children}</>;
}
